import { useState, useEffect } from "react";
import { MessageSquare, Phone, Mail, Clock } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminEnquiries = () => {
  const { token } = useAuth();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEnquiries();
  }, [token]);

  const fetchEnquiries = async () => {
    try {
      const response = await axios.get(`${API}/enquiries`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEnquiries(response.data);
    } catch (error) {
      toast.error("Failed to fetch enquiries");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-2 border-royal-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="admin-enquiries-page">
      <div className="mb-8">
        <p className="font-display text-xs tracking-[0.3em] uppercase text-royal-gold mb-2">Customer Leads</p>
        <h1 className="font-serif text-3xl text-cream">Bulk Enquiries</h1>
      </div>

      {enquiries.length === 0 ? (
        <div className="text-center py-20 bg-charcoal/30 border border-royal-gold/10">
          <MessageSquare size={48} className="mx-auto mb-4 text-cream/30" />
          <h3 className="font-serif text-xl text-cream mb-2">No Enquiries Yet</h3>
          <p className="font-body text-sm text-cream/50">
            Bulk order enquiries will appear here
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {enquiries.map((enquiry) => (
            <div
              key={enquiry.id}
              className="bg-charcoal/30 border border-royal-gold/10 p-6 hover:border-royal-gold/30 transition-colors"
            >
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="space-y-3 flex-1">
                  <h3 className="font-serif text-xl text-cream">{enquiry.name}</h3>
                  
                  <div className="flex flex-wrap gap-4 text-sm">
                    <a
                      href={`tel:${enquiry.phone}`}
                      className="flex items-center space-x-2 text-cream/70 hover:text-royal-gold transition-colors"
                    >
                      <Phone size={14} className="text-royal-gold" />
                      <span className="font-body">{enquiry.phone}</span>
                    </a>
                    {enquiry.email && (
                      <a
                        href={`mailto:${enquiry.email}`}
                        className="flex items-center space-x-2 text-cream/70 hover:text-royal-gold transition-colors"
                      >
                        <Mail size={14} className="text-silver" />
                        <span className="font-body">{enquiry.email}</span>
                      </a>
                    )}
                  </div>

                  {enquiry.message && (
                    <div className="mt-4 pt-4 border-t border-royal-gold/10">
                      <p className="font-display text-xs tracking-widest uppercase text-cream/40 mb-2">Requirement</p>
                      <p className="font-body text-cream/70 leading-relaxed">{enquiry.message}</p>
                    </div>
                  )}
                </div>

                <div className="flex flex-col items-end gap-3">
                  <div className="flex items-center space-x-2 text-cream/40 text-xs">
                    <Clock size={12} />
                    <span className="font-body">{formatDate(enquiry.created_at)}</span>
                  </div>
                  
                  <a
                    href={`https://wa.me/${enquiry.phone.replace(/[^0-9]/g, "")}?text=Hello ${enquiry.name}, thank you for your enquiry about our silver jewellery collection.`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-[#25D366]/20 border border-[#25D366]/50 text-[#25D366] text-xs font-display uppercase tracking-widest hover:bg-[#25D366]/30 transition-colors"
                  >
                    Reply on WhatsApp
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminEnquiries;
